import { Chip } from '../primitives';
import { colors } from '@munifocus/tokens';
import { usePortalPalette } from '../theme/usePortalPalette';

export interface ToggleChipProps {
  label: string;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

/**
 * A chip you pick: one statutory authority in a set of them, one permission.
 *
 * **Why not a checkbox.** These sets run to a dozen short codes chosen together
 * — the authorities on an appointment, say — and a column of checkboxes turns a
 * two-line control into half a screen. As chips they read as one field.
 *
 * The state is carried by fill *and* by the control's `aria-pressed`, never by
 * colour alone: which authorities an officer holds is the difference between a
 * lawful entry and an unlawful one, and it is not a thing to convey only to
 * readers who can tell the accent from grey (WCAG 1.4.1).
 *
 * Three screens had written this out — the appointment dialog, the officer
 * editor and the user editor — with the same eight lines of `color={...}
 * variant={...}` ternaries, and none of them told a screen reader anything at
 * all about what was selected.
 */
export function ToggleChip({ label, selected, onToggle, disabled }: ToggleChipProps) {
  const portal = usePortalPalette();

  return (
    <Chip
      size="small"
      label={label}
      clickable={!disabled}
      disabled={disabled}
      onClick={disabled ? undefined : onToggle}
      aria-pressed={selected}
      role="button"
      sx={{
        height: 24,
        fontSize: '0.72rem',
        fontWeight: 600,
        backgroundColor: selected ? portal.brand.accent : 'transparent',
        color: selected ? colors.common.white : colors.text.secondary,
        border: '1px solid',
        borderColor: selected ? portal.brand.accent : colors.border.interactive,
        '&:hover': {
          backgroundColor: selected ? portal.brand.rail : colors.surface.cream2,
        },
      }}
    />
  );
}
